import React from "react";
import { observer, inject } from "mobx-react";
import ServicePlanStore from "../../ServicePlan/ServicePlanStore";
import ServicePlanModelSelector from "../../ServicePlan/components/ServicePlanModelSelector";
import ServicePlanPriceTable from "../../ServicePlan/components/ServicePlanPriceTable";
import ServicePlanPriceGuide from "../../ServicePlan/components/ServicePlanPriceGuide";
import Loader from "../../Loader";

interface CappedPriceServiceProps {}

interface InjectedProps extends CappedPriceServiceProps {
  servicePlanStore: ServicePlanStore;
}

interface CappedPriceServiceState {
  isContentExist: boolean;
}

@inject("servicePlanStore")
@observer
class CappedPricing extends React.Component<
  CappedPriceServiceProps,
  CappedPriceServiceState
> {
  constructor(props: CappedPriceServiceProps) {
    super(props);
    this.state = {
      isContentExist: false,
    };
  }

  get injected() {
    return this.props as InjectedProps;
  }

  componentDidMount(): void {
    const { servicePlanStore } = this.injected;
    servicePlanStore.fetchCappedPricingContentData();

    this.setState({
      isContentExist:
        servicePlanStore.cappedPricingCMSModel.response.length > 0 ||
        (servicePlanStore.cappedPricingCpsTabs?.length ?? 0) > 0,
    });

    servicePlanStore.fetchCappedServices();

    window["pushDigitalData"]({
      event: "_pageLoaded",
    });
  }

  handleModelChange = (target, model) => {
    target.preventDefault();

    const { servicePlanStore } = this.injected;
    servicePlanStore.setModelVariantSelected("");
    servicePlanStore.setCappedPricingSelectedModel(model.modelID);
  };

  handleTabChange = (tab) => {
    const { servicePlanStore } = this.injected;
    servicePlanStore.setModelVariantSelected("");
    servicePlanStore.setSelectedCappedPricingTabId(tab.tabId);
    if (tab.modelId) servicePlanStore.setCappedPricingSelectedModel(tab.modelId);
  };

  handleModelVariantChange = (target) => {
    target.preventDefault();
    const { servicePlanStore } = this.injected;
    servicePlanStore.setModelVariantSelected(target.currentTarget.text);
  };

  render() {
    const { servicePlanStore } = this.injected;

    // Mirror ServicePlanContent: use computed getters for proper MobX tracking
    const cappedPricingResponse = servicePlanStore.cappedPricing.response;
    const cappedPricingIsRequestError = servicePlanStore.cappedPricing.isRequestError;
    const cappedPricingIsServiceIntervalExist = servicePlanStore.cappedPricing.isServiceIntervalExist;

    const hasTabs = (servicePlanStore.cappedPricingCpsTabs?.length ?? 0) > 0;
    const isContentExist = this.state.isContentExist || hasTabs;

    return (
      isContentExist && (
        <div className="container">
          <p></p>
          <div className="cappedPricingTableHeading">
            <h4>Capped Price Servicing Costs</h4>
            <p>
              If you are looking to buy a new Subaru, please select the model
              you are interested in to view Capped Price Servicing costs.
            </p>
          </div>
          <div className="capped-pricing-desktop">
            {isContentExist ? (
              <ServicePlanModelSelector
                vehicleModels={servicePlanStore.cappedPricingCMSModel.response}
                handleModelChange={(e, model) => {
                  this.handleModelChange(e, model);
                }}
                selectedModel={servicePlanStore.cappedPricingSelectedModel}
                cpsTabs={servicePlanStore.cappedPricingCpsTabs}
                selectedTabId={servicePlanStore.selectedCappedPricingTabId}
                onTabChange={(tab) => this.handleTabChange(tab)}
              />
            ) : (
              <p>Vehicle models are not configured for CappedPricing tool.</p>
            )}

            {isContentExist && cappedPricingResponse.length > 0 ? (
              <ServicePlanPriceTable
                selectedModel={servicePlanStore.cappedPricingSelectedModel}
                isServicePlan={false}
                handleModelVariantChange={(e) => {
                  this.handleModelVariantChange(e);
                }}
              />
            ) : cappedPricingIsRequestError ? (
              <p className="text-center">
                An error occurred while loading pricing data. Please try again
                later.
              </p>
            ) : cappedPricingIsServiceIntervalExist ? (
              <p className="text-center">
                Pricing is not available for this model.
              </p>
            ) : (
              <Loader isLoading={true} isPositionRelative={false} />
            )}
          </div>
          <div className="capped-pricing-mobile">
            {cappedPricingResponse.length > 0 ? (
              <ServicePlanPriceGuide
                isServicePlan={false}
                onSelectedModelChange={(e) => {
                  servicePlanStore.setCappedPricingSelectedModel(e.modelID);
                }}
                selectedModel={servicePlanStore.cappedPricingSelectedModel}
                vehicleModelsListForSelectControl={
                  servicePlanStore.vehicleModelsListForSelectControlCPS
                }
                vehicleModelDataList={cappedPricingResponse}
                cmsContent={servicePlanStore.cappedPricingCMSModel.response}
                showTotals={false}
                selectedModelVariant={servicePlanStore.modelVariantsSelected}
                handleModelVariantChange={(e) => {
                  this.handleModelVariantChange(e);
                }}
                cpsTabs={servicePlanStore.cappedPricingCpsTabs}
                selectedTabId={servicePlanStore.selectedCappedPricingTabId}
                onTabChange={(tab) => this.handleTabChange(tab)}
              />
            ) : cappedPricingIsRequestError ? (
              <p className="text-center">
                An error occurred while loading pricing data. Please try again
                later.
              </p>
            ) : cappedPricingIsServiceIntervalExist ? (
              <p className="text-center">
                Pricing is not available for this model.
              </p>
            ) : (
              <Loader isLoading={true} isPositionRelative={false} />
            )}
          </div>
        </div>
      )
    );
  }
}

export default CappedPricing;
